/// <amd-module name="Artifacts/Feed/components/ArtifactsSettingsPanel" />
import { ArtifactsSettingsActions } from "Artifacts/Feed/redux/Actions";
import { IFeedModuleState } from "Artifacts/Feed/redux/Module";
import * as Res from "Artifacts/Feed/Resources";
import { IArtifactsSettingsData } from "Artifacts/Feed/services/ArtifactsSettingsService";
import { CenteredSpinner } from "Artifacts/Root/components/CenteredSpinner";
import { DefaultLoader } from "Artifacts/Root/components/DefaultLoader";
import { ErrorBannerLoader } from "Artifacts/Root/components/ErrorBannerLoader";
import { ComponentState } from "Artifacts/Root/Constants";
import { isLoading } from "Artifacts/Root/helpers/ComponentStateHelpers";
import { useFeed } from "Artifacts/Root/hooks/FeedUrlResolver";
import * as RootRes from "Artifacts/Root/Resources";
import { IFeed } from "Artifacts/Root/types";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { useComponentContext } from "VSS/Platform/Layout";
import { IdentityPicker, IIdentity } from "VSSUI/IdentityPicker";
import { Panel } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";

export interface IArtifactsSettingsPanelProps {
  onDismiss: () => void;
}

interface IArtifactsSettingsPanelMapStateToProps {
  componentState: ComponentState;
  saveButtonComponentState: ComponentState;
  serviceParams?: IArtifactsSettingsData;
}

const Actions = {
  saveArtifactsSettingsAction: ArtifactsSettingsActions.saveArtifactsSettings,
  loadArtifactsSettingsParams: ArtifactsSettingsActions.loadArtifactsSettingsParams,
};

interface IArtifactsSettingsPanelState {
  everyoneIsCreator: boolean;
  creatorIds: IIdentity[];
  adminIds: IIdentity[];
  customNames: string[];
}

function getInitialFormState(
  serviceParams: IArtifactsSettingsData | undefined,
): IArtifactsSettingsPanelState {
  return {
    everyoneIsCreator: serviceParams?.everyoneIsCreator || false,
    creatorIds: serviceParams ? [...serviceParams.creatorIds] : [],
    adminIds: serviceParams ? [...serviceParams.adminIds] : [],
    customNames: serviceParams ? [...serviceParams.customNames] : [],
  };
}

const ArtifactsSettingsPanel: React.FC<IArtifactsSettingsPanelProps &
  IArtifactsSettingsPanelMapStateToProps &
  typeof Actions> = ({
  onDismiss,
  componentState,
  saveButtonComponentState,
  serviceParams,
  saveArtifactsSettingsAction,
  loadArtifactsSettingsParams,
}) => {
  const { feed, resolution } = useFeed();

  useEffect(() => {
    loadArtifactsSettingsParams(feed);
  }, [feed, loadArtifactsSettingsParams]);

  const [panelState, setFormState] = useState<IArtifactsSettingsPanelState>(() =>
    getInitialFormState(serviceParams),
  );

  useEffect(() => {
    if (serviceParams) {
      setFormState(getInitialFormState(serviceParams));
    }
  }, [serviceParams]);

  const createFeedEveryone = "everyone-create-feed";
  const createFeedSelected = "selected-create-feed";

  const isValidForm = (panelState: IArtifactsSettingsPanelState): boolean => {
    if (!panelState.everyoneIsCreator && panelState.creatorIds?.length === 0) {
      return false;
    }
    return true;
  };

  const saveArtifactsSettings = useCallback(
    (feed: IFeed | undefined) => {
      if (isValidForm(panelState)) {
        const handleSaveSuccessful = () => {
          onDismiss();
        };

        const settingsData: IArtifactsSettingsData = {
          adminIds: panelState.adminIds,
          creatorIds: panelState.everyoneIsCreator ? [] : panelState.creatorIds,
          everyoneIsCreator: panelState.everyoneIsCreator,
          customNames: [], // these can't be modified
        };
        feed && saveArtifactsSettingsAction(settingsData, feed, handleSaveSuccessful);
      }
    },
    [saveArtifactsSettingsAction, panelState, onDismiss],
  );

  const { pageContext } = useComponentContext();

  const peoplePickerProvider = new PeoplePickerProvider(pageContext, {
    identityTypes: ["user", "group"],
    operationScopes: ["ims"],
    options: {
      MinResults: 5,
      MaxResults: 20,
    },
  });

  return (
    <Panel
      className="artifacts-settings"
      titleProps={{
        text: Res.ArtifactsSettings_PageTitle,
        id: "artifacts-settings-panel-title",
      }}
      onDismiss={/** Handle resetting the error state here so message bar is dismissed*/ onDismiss}
      overlayContent={
        isLoading(saveButtonComponentState) ? (
          <CenteredSpinner label={RootRes.Submitting} />
        ) : isLoading(componentState) ? (
          <CenteredSpinner />
        ) : (
          undefined
        )
      }
      footerButtonProps={[
        { text: Res.Dialog_CancelButtonText, onClick: onDismiss },
        {
          text: Res.SaveButton,
          primary: true,
          disabled: !isValidForm(panelState),
          onClick: () => {
            saveArtifactsSettings(feed);
          },
        },
      ]}
    >
      <DefaultLoader state={resolution}>
        <ErrorBannerLoader state={saveButtonComponentState} noLoadingSpinner>
          <div>
            <CreatorPickerContent
              createFeedEveryone={createFeedEveryone}
              createFeedSelected={createFeedSelected}
              panelState={panelState}
              setFormState={setFormState}
              peoplePickerProvider={peoplePickerProvider}
            />
            <AdministratorPickerContent
              panelState={panelState}
              setFormState={setFormState}
              peoplePickerProvider={peoplePickerProvider}
            ></AdministratorPickerContent>
            <CustomNamesListContent panelState={panelState}></CustomNamesListContent>
          </div>
        </ErrorBannerLoader>
      </DefaultLoader>
    </Panel>
  );
};

export interface ICreatorPickerContentProps {
  createFeedEveryone: string;
  createFeedSelected: string;
  panelState: IArtifactsSettingsPanelState;
  setFormState: React.Dispatch<React.SetStateAction<IArtifactsSettingsPanelState>>;
  peoplePickerProvider: PeoplePickerProvider;
}

const CreatorPickerContent: React.FC<ICreatorPickerContentProps> = ({
  createFeedEveryone,
  createFeedSelected,
  panelState,
  setFormState,
  peoplePickerProvider,
}) => {
  const creatorRadioSelection = panelState.everyoneIsCreator
    ? createFeedEveryone
    : createFeedSelected;

  const onCreatorIdentityAdded = (
    identity: IIdentity,
    panelState: IArtifactsSettingsPanelState,
    setFormState: React.Dispatch<React.SetStateAction<IArtifactsSettingsPanelState>>,
  ): void => {
    setFormState({ ...panelState, creatorIds: [...panelState.creatorIds, identity] });
  };

  const onCreatorIdentitiesRemoved = (
    identities: IIdentity[],
    panelState: IArtifactsSettingsPanelState,
    setFormState: React.Dispatch<React.SetStateAction<IArtifactsSettingsPanelState>>,
  ): void => {
    const filteredCreatorIds = panelState.creatorIds.filter(
      (entity) =>
        identities.filter((identity) => identity.entityId === entity.entityId).length === 0,
    );
    setFormState({ ...panelState, creatorIds: filteredCreatorIds });
  };

  return (
    <div className="artifacts-settings-panel-section">
      <h2 className="title-s">{Res.ArtifactsSettings_CreatePermissionsText}</h2>
      <div>
        <RadioButtonGroup
          className="select-feed-creators-radio-buttons"
          selectedButtonId={creatorRadioSelection}
          onSelect={(buttonId) =>
            buttonId === createFeedEveryone
              ? setFormState({ ...panelState, everyoneIsCreator: true })
              : setFormState({ ...panelState, everyoneIsCreator: false })
          }
        >
          <RadioButton
            key={createFeedEveryone}
            id={createFeedEveryone}
            text={Res.ArtifactsSettings_FeedCreatePermissionsAll}
          />
          <RadioButton
            key={createFeedSelected}
            id={createFeedSelected}
            text={Res.ArtifactsSettings_FeedCreatePermissionsAdmin}
          />
        </RadioButtonGroup>
        {!panelState.everyoneIsCreator && (
          <IdentityPicker
            pickerProvider={peoplePickerProvider}
            onIdentityAdded={(identity) =>
              onCreatorIdentityAdded(identity, panelState, setFormState)
            }
            onIdentitiesRemoved={(identities) =>
              onCreatorIdentitiesRemoved(identities, panelState, setFormState)
            }
            onIdentityRemoved={(identity) =>
              onCreatorIdentitiesRemoved([identity], panelState, setFormState)
            }
            selectedIdentities={panelState.creatorIds}
          />
        )}
      </div>
    </div>
  );
};

export interface IAdministratorPickerContentProps {
  panelState: IArtifactsSettingsPanelState;
  setFormState: React.Dispatch<React.SetStateAction<IArtifactsSettingsPanelState>>;
  peoplePickerProvider: PeoplePickerProvider;
}

const AdministratorPickerContent: React.FC<IAdministratorPickerContentProps> = ({
  panelState,
  setFormState,
  peoplePickerProvider,
}) => {
  const onAdminIdentityAdded = (
    identity: IIdentity,
    panelState: IArtifactsSettingsPanelState,
    setFormState: React.Dispatch<React.SetStateAction<IArtifactsSettingsPanelState>>,
  ): void => {
    setFormState({ ...panelState, adminIds: [...panelState.adminIds, identity] });
  };

  const onAdminIdentitiesRemoved = (
    identities: IIdentity[],
    panelState: IArtifactsSettingsPanelState,
    setFormState: React.Dispatch<React.SetStateAction<IArtifactsSettingsPanelState>>,
  ): void => {
    const filteredAdminIds = panelState.adminIds.filter(
      (entity) =>
        identities.filter((identity) => identity.entityId === entity.entityId).length === 0,
    );
    setFormState({ ...panelState, adminIds: filteredAdminIds });
  };

  return (
    <div className="artifacts-settings-panel-section">
      <h2 className="title-s">{Res.ArtifactsSettings_AdminPermissions_Text}</h2>
      <div className="artifacts-settings-panel-description">
        {Res.ArtifactsSettings_FeedAdminExplanation}
      </div>
      <IdentityPicker
        pickerProvider={peoplePickerProvider}
        onIdentityAdded={(identity) => onAdminIdentityAdded(identity, panelState, setFormState)}
        onIdentitiesRemoved={(identities) =>
          onAdminIdentitiesRemoved(identities, panelState, setFormState)
        }
        onIdentityRemoved={(identity) =>
          onAdminIdentitiesRemoved([identity], panelState, setFormState)
        }
        selectedIdentities={panelState.adminIds}
      />
    </div>
  );
};

export interface ICustomNamesListContentProps {
  panelState: IArtifactsSettingsPanelState;
}

const CustomNamesListContent: React.FC<ICustomNamesListContentProps> = ({ panelState }) => {
  // The custom names list is only shown if there are custom permissions set - this is very unlikely
  if (panelState.customNames.length > 0) {
    return (
      <div className="feed-global-settings-panel-section">
        <h2 className="title-s">{Res.ArtifactsSettings_CustomPermissions_Text}</h2>
        <div className="feed-global-settings-panel-description">
          {Res.ArtifactsSettings_FeedCustomExplanation}
        </div>
        <NameList names={panelState.customNames} />
      </div>
    );
  }
  return <div />;
};

interface INameListProps {
  names: string[];
}

// Unordered list of names
function NameList(props: INameListProps): JSX.Element {
  const names = props.names;
  const listItems = names.map((name, index) => <li key={index}>{name}</li>);
  return (
    <div>
      <ul>{listItems}</ul>
    </div>
  );
}

const mapStateToProps = (state: IFeedModuleState): IArtifactsSettingsPanelMapStateToProps => {
  const panelState = state["feed"].artifactsSettingsPanel;
  return {
    componentState: panelState.componentState,
    saveButtonComponentState: panelState.saveButtonComponentState,
    serviceParams: panelState.serviceParams,
  };
};

const ConnectedArtifactsSettingsPanel = connect(
  mapStateToProps,
  Actions,
)(ArtifactsSettingsPanel);

export { ConnectedArtifactsSettingsPanel as ArtifactsSettingsPanel };
